import { useState } from 'react';
import { currencySymbol, ICreatePaymentPromiseRequest } from 'shared';

import { Button } from '../../../common/Atoms/Button';
import { CheckBox } from '../../../common/Atoms/CheckBox';
import { CurrencyInput } from '../../../common/Atoms/CurrencyInput';
import { DatePicker } from '../../../common/Atoms/DatePicker';
import { TextArea } from '../../../common/Atoms/TextArea';
import { useSettings } from '../../hooks/useSettings';

interface PaymentPromiseFormProps {
  amountDueCents: number;
  currency: string;
  onSubmit: (promise: ICreatePaymentPromiseRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

/** Record a payment promise. Shared by the invoice page and escalations. */
export function PaymentPromiseForm({
  amountDueCents,
  currency,
  onSubmit,
  onCancel,
  loading,
}: PaymentPromiseFormProps) {
  const { data: settings } = useSettings();
  const [promisedDate, setPromisedDate] = useState<string>(``);
  const [amountCents, setAmountCents] = useState<number>(amountDueCents);
  const [updateExpectedPaymentDate, setUpdateExpectedPaymentDate] = useState<boolean>(!!settings?.promiseUpdatesExpectedPaymentDate);
  const [notes, setNotes] = useState<string>(``);

  const amountInvalid = !Number.isInteger(amountCents) || amountCents < 1 || amountCents > amountDueCents;
  const canSubmit = !!promisedDate && !amountInvalid && !loading;

  async function submit() {
    await onSubmit({
      promisedDate,
      promisedAmountCents: amountCents,
      updateExpectedPaymentDate,
      notes: notes || null,
    });
  }

  return (
    <div className={ `space-y-3` }>
      <DatePicker
        label={ `Promised payment date` }
        name={ `promisedDate` }
        value={ promisedDate }
        onChange={ ({ value }) => setPromisedDate(value || ``) }
        disableClear
      />
      <CurrencyInput
        label={ `Promised amount` }
        name={ `promisedAmountCents` }
        prefix={ currencySymbol(currency) }
        value={ amountCents }
        onChange={ ({ value }) => setAmountCents(Number(value)) }
        description={ amountInvalid ? `Must be between 0.01 and the amount due` : undefined }
      />
      <CheckBox
        label={ `Also set the expected payment date` }
        name={ `updateExpectedPaymentDate` }
        checked={ updateExpectedPaymentDate }
        onChange={ ({ value }) => setUpdateExpectedPaymentDate(value) }
        description={ `Syncs the promised date to your accounting system as the expected payment date` }
      />
      <TextArea
        label={ `Notes` }
        name={ `notes` }
        value={ notes }
        rows={ 2 }
        onChange={ ({ value }) => setNotes(String(value)) }
      />
      <div className={ `flex justify-end gap-2` }>
        <Button color={ `none` }
          onClick={ onCancel }
          disabled={ loading }>
          { `Cancel` }
        </Button>
        <Button onClick={ submit }
          loading={ loading }
          disabled={ !canSubmit }>
          { `Record promise` }
        </Button>
      </div>
    </div>
  );
}
